import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { Reveal } from "@/components/site/Reveal";
import api from "@/lib/api";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };

export default function InsightDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(undefined);
  const [more, setMore] = useState([]);

  useEffect(() => {
    setA(undefined);
    api.getInsight(slug).then(setA).catch(() => setA(null));
    api.getInsights().then((all) => setMore(all.filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (a === null) return <Navigate to="/insights" replace />;
  if (!a) return <div className="min-h-screen" />;

  return (
    <article data-testid="insight-detail-page">
      <PageHero breadcrumb={[{ label: "Insights", to: "/insights" }, { label: a.category }]} eyebrow={a.category} title={a.title} compact>
        <div className="flex items-center gap-4 text-[13px] text-[color:var(--bp-text-muted)]">
          <span>{a.author}</span><span>·</span><span>{fmtDate(a.date)}</span><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.reading_time} min read</span>
        </div>
      </PageHero>

      <section className="pb-8">
        <div className="container-bp max-w-4xl">
          <Reveal>
            <div className="aspect-[16/8] overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)]">
              <img src={a.image} alt={a.title} className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-bp max-w-3xl">
          <div className="space-y-6">
            {a.body.map((p, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <p className={i === 0 ? "text-[18px] leading-[1.8] text-[color:var(--bp-cream)]" : "text-[16px] leading-[1.85] text-[color:var(--bp-text-muted)]"}>{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 flex items-center justify-between border-t border-[color:var(--bp-border)] pt-8">
            <Link to="/insights" className="inline-flex items-center gap-2 text-[14px] font-medium text-[color:var(--bp-teal)]"><ArrowLeft className="h-4 w-4" /> All insights</Link>
          </div>
        </div>
      </section>

      {more.length > 0 && (
        <section className="section-py bg-[color:var(--bp-bg-2)]">
          <div className="container-bp">
            <h2 className="font-display text-2xl font-semibold text-[color:var(--bp-cream)]">Keep reading</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {more.map((m) => (
                <Link key={m.slug} to={`/insights/${m.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--bp-teal)]/50">
                  <div className="aspect-[16/10] overflow-hidden"><img src={m.image} alt={m.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bp-teal)]">{m.category}</span>
                    <h3 className="mt-2 font-display text-base font-semibold text-[color:var(--bp-cream)]">{m.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[color:var(--bp-teal)]">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand title="Want this intelligence applied to your team?" />
    </article>
  );
}
